import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthContext } from '../contexts/auth';
import { useAuthRedirect } from './useAuthRedirect';
import { isOperatorAdmin, isOperatorStaff } from '../utils/permission';
import type { UserSetType } from '../types/user';

type UseAdminPageAccessControlReturn = {
  user: UserSetType | null;
  noPermission: boolean;
  isChecking: boolean;
};

/**
 * 運営管理ページのアクセス制御フック
 * requiredRole='admin'（デフォルト）:
 *   - 企業ユーザー（role=1, 2）: 即 /home にリダイレクト
 *   - 運営オペレーター（role=11）: noPermission=true を返す（企業詳細へ戻るボタン表示用）
 *   - 運営管理者（role=10）: アクセス許可
 * requiredRole='staff':
 *   - 企業ユーザー（role=1, 2）: 即 /home にリダイレクト
 *   - 運営スタッフ（role=10, 11）: アクセス許可
 */
export const useAdminPageAccessControl = (
  pageName: string,
  requiredRole: 'admin' | 'staff' = 'admin',
): UseAdminPageAccessControlReturn => {
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const [noPermission, setNoPermission] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      return;
    }

    // 企業ユーザーは即リダイレクト
    if (!isOperatorStaff(user.role)) {
      navigate('/home');
      return;
    }

    // requiredRole='admin' の場合、運営管理者のみアクセス可能（運営オペレーターは権限なし画面を表示）
    if (requiredRole === 'admin' && !isOperatorAdmin(user.role)) {
      Sentry.captureMessage(`Unauthorized access to ${pageName}`, {
        level: 'warning',
        extra: { userId: user.uid, role: user.role },
      });
      setNoPermission(true);
    } else {
      setNoPermission(false);
    }

    setIsChecking(false);
  }, [isLoading, user, navigate, pageName, requiredRole]);

  return { user, noPermission, isChecking };
};
